import { useCallback, useMemo } from 'react';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

/**
 * Reads and writes the report state the Reports page uses as its source of truth.
 *
 * `ReportId` lives in the URL **path** (`/reports/:reportId`); the remaining
 * params (ScopeId, ScopeObjectId, ScopeObjectName, TestName) live in the query
 * string. Numeric values are coerced to `number | null`; string values to
 * `string | null`. Pass `undefined`/`null`/`''` to `setParams` to delete a key.
 * `clearAll` wipes all keys.
 */
export interface ReportUrlState {
    reportId: number | null;
    scopeId: number | null;
    scopeObjectId: number | null;
    scopeObjectName: string | null;
    testName: string | null;
}

export type ReportUrlParamUpdates = Partial<Record<
    'ReportId' | 'ScopeId' | 'ScopeObjectId' | 'ScopeObjectName' | 'TestName',
    string | number | null | undefined
>>;

export interface UseReportUrlStateResult extends ReportUrlState {

    /** Delete every report-related URL key. */
    clearAll: () => void;

    /**
     * Clear only report + filter params, preserving scope (ScopeId,
     * ScopeObjectId, ScopeObjectName) so the user doesn't have to
     * re-pick a partner when navigating back to the landing page.
     */
    clearReport: () => void;

    /** Raw URLSearchParams, useful for serializing into `ReportApi.CreateUpdateCustomReport`. */
    rawSearchParams: URLSearchParams;

    /** Merge updates into the URL. `undefined`/`null`/`''` deletes the key. */
    setParams: (updates: ReportUrlParamUpdates, options?: { replace?: boolean }) => void;
}

const SCOPE_URL_KEYS = ['ScopeId', 'ScopeObjectId', 'ScopeObjectName'] as const;

/** Read the report id currently encoded in `/reports/:reportId`, if any. */
const readReportIdFromPath = (): string | null => {

    const match = window.location.pathname.match(/\/reports\/(\d+)/i);
    return match ? match[1] : null;

};

/** Build a `/reports` or `/reports/:reportId` location, carrying `search` along. */
const buildReportLocation = (reportId: string | null, search: URLSearchParams) => {

    const query = search.toString();
    return {
        pathname: reportId ? `/reports/${reportId}` : '/reports',
        search: query ? `?${query}` : ''
    };

};

export const useReportUrlState = (): UseReportUrlStateResult => {

    const [searchParams] = useSearchParams();
    const params = useParams();
    const navigate = useNavigate();

    const state = useMemo<ReportUrlState>(() => {

        const reportId = params.reportId ?? null;
        const scopeId = searchParams.get('ScopeId');
        const scopeObjectId = searchParams.get('ScopeObjectId');

        return {
            reportId: reportId ? Number(reportId) : null,
            scopeId: scopeId ? Number(scopeId) : null,
            scopeObjectId: scopeObjectId ? Number(scopeObjectId) : null,
            scopeObjectName: searchParams.get('ScopeObjectName'),
            testName: searchParams.get('TestName')
        };

    }, [params.reportId, searchParams]);

    const setParams = useCallback((updates: ReportUrlParamUpdates, options?: { replace?: boolean }) => {

        const next = new URLSearchParams(window.location.search);
        let reportId = readReportIdFromPath();

        Object.entries(updates).forEach(([key, value]) => {

            const isEmpty = value === undefined || value === null || value === '';

            if (key === 'ReportId') {

                reportId = isEmpty ? null : String(value);
                return;

            }

            if (isEmpty) {

                next.delete(key);

            } else {

                next.set(key, String(value));

            }

        });

        navigate(buildReportLocation(reportId, next), options);

    }, [navigate]);

    const clearAll = useCallback(() => {

        navigate(buildReportLocation(null, new URLSearchParams()));

    }, [navigate]);

    const clearReport = useCallback(() => {

        const live = new URLSearchParams(window.location.search);
        const next = new URLSearchParams();
        SCOPE_URL_KEYS.forEach(k => {

            const v = live.get(k);
            if (v) next.set(k, v);

        });
        navigate(buildReportLocation(null, next));

    }, [navigate]);

    return { ...state, clearAll, clearReport, rawSearchParams: searchParams, setParams };

};
